/* ============================================================
   BUILDER ADD-ON CHECKBOX
   Companion to builder-addons.css. Loaded only by
   sections/main-product.liquid, so the dedicated builder templates
   (sections/main-product-bundle.liquid) are untouched.

   The design makes the checkbox a control: it is what adds and removes the
   add-on. The theme ships that same input as .sr-only, and
   bundle-variant-selector.js only ever writes it from its own buttons — the
   one listener it attaches just re-runs updateAddToCartState(). So once the
   stylesheet makes the box visible, a user click would untick the row while
   the running total still counted the item, and the shopper would be quoted a
   price the cart never charges.

   This routes the click back into the controller's own actions rather than
   reimplementing any of its state handling.
   ============================================================ */
(function () {
  'use strict';

  /* Every collapse path ends by smooth-scrolling the page back to the row —
     closeOptions() calls scrollToCardHeader when it finishes, and Add
     Selection wraps its collapse inside the same scroll. The shopper is
     already looking at the row, so the jump is just disorienting. Replace the
     method with one that skips the scroll but still runs the callback
     immediately: applySelection's collapse lives in that callback, so
     swallowing it here would break the collapse itself. */
  if (typeof BundleVariantSelector === 'function') {
    BundleVariantSelector.prototype.scrollToCardHeader = function (card, callback) {
      if (typeof callback === 'function') callback();
    };

    /* The open animation takes its transition from the stylesheet (one 0.5s
       curve — see builder-addons.css), but closeOptions pins the theme's
       staggered 0.8s/0.6s/0.7s transition INLINE, which outranks any
       stylesheet. Rewrite it right after the original sets it — this runs
       before the 20ms timeout that kicks off the animation, so the collapse
       runs on the same single curve as the open. finalizeClose clears the
       inline value afterwards, so nothing leaks into the next open. */
    var UNIFIED_TRANSITION =
      'height 0.5s cubic-bezier(0.25, 1, 0.5, 1), ' +
      'opacity 0.5s cubic-bezier(0.25, 1, 0.5, 1), ' +
      'transform 0.5s cubic-bezier(0.25, 1, 0.5, 1)';
    var originalCloseOptions = BundleVariantSelector.prototype.closeOptions;
    BundleVariantSelector.prototype.closeOptions = function (card, options, shouldScroll) {
      originalCloseOptions.call(this, card, options, shouldScroll);
      if (options && !options.hidden) {
        options.style.transition = UNIFIED_TRANSITION;
      }
    };
  }

  /* applySelection() ends by scrolling to the row and then collapsing it. The
     design keeps the picker open while you try colours, so the collapse is
     suppressed by stubbing the one method that schedules it — the callback that
     closes the panel is handed to scrollToCardHeader, so a no-op swallows it
     without touching any of the state applySelection just wrote.

     BundleVariantSelector is a top-level class in a classic script, so the
     binding is reachable here by name (it is never put on window). */
  function applyKeepingOpen(apply) {
    var proto = typeof BundleVariantSelector === 'function' ? BundleVariantSelector.prototype : null;
    if (!proto || typeof proto.scrollToCardHeader !== 'function') {
      apply.click();
      return;
    }
    var scrollToCardHeader = proto.scrollToCardHeader;
    proto.scrollToCardHeader = function () {};
    try {
      apply.click();
    } finally {
      proto.scrollToCardHeader = scrollToCardHeader;
    }
  }

  /* Choosing a variant is intent to add it. The controller only previews on
     picker change and waits for Add Selection, which this design doesn't have. */
  document.addEventListener('change', function (event) {
    var picker = event.target.closest(
      '.product-builder-block variant-picker input[type="radio"], .product-builder-block variant-picker select'
    );
    if (!picker) return;

    var card = picker.closest('.product-builder__card');
    if (!card) return;

    var apply = card.querySelector('.product-builder__options-apply');
    if (!apply || apply.disabled) return;

    applyKeepingOpen(apply);
  });

  /* `click`, not `change`, on purpose. The controller sets `checked` itself and
     dispatches a synthetic `change` (applySelection, removeSelection,
     selectSingleVariant all do). Listening for `change` meant this handler
     answered the controller's own events and re-entered apply mid-collapse,
     which left the row reading the new variant while the total never counted
     it. A programmatic `.checked = x` never produces a `click`, so this fires
     for real user interaction only. */
  document.addEventListener('click', function (event) {
    var input = event.target.closest('.product-builder-block .product-builder__input');
    if (!input) return;

    var card = input.closest('.product-builder__card');
    if (!card) return;

    if (input.checked) {
      /* Single-variant items have no picker and carry a select button instead. */
      var apply = card.querySelector('.product-builder__options-apply');
      if (apply) {
        applyKeepingOpen(apply);
      } else {
        var select = card.querySelector('.product-builder__select-btn');
        if (select) select.click();
      }
    } else {
      var remove = card.querySelector('.product-builder__remove-btn');
      if (remove) remove.click();
    }

    /* If the action declined — an unavailable variant leaves Add Selection
       disabled — the box must not be left saying otherwise. */
    input.checked = card.classList.contains('selected');
  });

  /* ---- thumbnail opens the product-details modal -----------------------
     The row already renders a quick-view trigger (the "View product details"
     button); the thumbnail borrows it, so the theme's whole modal flow —
     aria-controls lookup, focus trap, close handling — stays untouched.

     A selected row's modal should show the selection, so the quick-view's
     data-product-url gets the row's current variant appended before the
     modal opens. Capture phase, because the theme's own trigger listener
     reads that attribute synchronously on click — a bubble-phase update
     would land after the fetch URL was already built. */
  document.addEventListener(
    'click',
    function (event) {
      var thumb = event.target.closest('.product-builder-block .product-builder__image-container');
      var trigger = event.target.closest('.product-builder-block [data-product-builder-quick-view]');
      var origin = thumb || trigger;
      if (!origin) return;

      var card = origin.closest('.product-builder__card');
      if (!card) return;
      if (!trigger) trigger = card.querySelector('[data-product-builder-quick-view]');
      if (!trigger) return;

      var quickView = document.getElementById(trigger.getAttribute('aria-controls') || '');
      if (quickView) {
        var base = (quickView.getAttribute('data-product-url') || '').split('?')[0];
        var variantId = card.classList.contains('selected') ? card.dataset.currentVariantId : '';
        quickView.setAttribute('data-product-url', variantId ? base + '?variant=' + variantId : base);
      }

      /* The trigger's own click was the user's click; only the thumbnail
         needs the synthetic one. (That synthetic click re-enters this
         handler as a plain trigger click and just re-writes the same URL.) */
      if (thumb) trigger.click();
    },
    true
  );

  /* theme.js's QuickView.quickview() strips the query off data-product-url
     before appending ?view=modal, so the variant set above would never reach
     the server. Same body as the theme's, with the variant param preserved. */
  if (typeof QuickView === 'function' && QuickView.prototype.quickview) {
    QuickView.prototype.quickview = function () {
      const drawerContent = this.querySelector(this.selector);
      const rawUrl = this.getAttribute('data-product-url');
      const productUrl = rawUrl.split('?')[0];
      const variantMatch = rawUrl.match(/[?&]variant=(\d+)/);
      const sectionUrl = `${productUrl}?view=modal${variantMatch ? `&variant=${variantMatch[1]}` : ''}`;

      fetch(sectionUrl)
        .then(response => response.text())
        .then(responseText => {
          setTimeout(() => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = parsedHTML.querySelector(this.selector);
            this.setInnerHTML(drawerContent, productElement.innerHTML);
            theme.a11y.trapFocus(this, this.focusElement);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            document.dispatchEvent(new CustomEvent('quickview:loaded', {
              detail: {
                productUrl: this.getAttribute('data-product-url')
              }
            }));
          }, 200);
        })
        .catch(e => {
          console.error(e);
        });
    };
  }
})();
